import { apiClient } from '../../core/api/client';
import type { ApiModel } from '../../core/api/types';

export interface OfficialModelOption {
  label: string;
  slug: string;
  displayName: string;
  contextTokens: number;
  maxOutputTokens: number;
}

export interface OfficialImageOption {
  label: string;
  slug: string;
  displayName: string;
}

export function isOfficialImageModel(model: ApiModel): boolean {
  return model.protocol === 'openai-image' || model.capabilities.includes('image');
}

export function officialModelOption(model: ApiModel): OfficialModelOption {
  const label = model.displayName && model.displayName !== model.slug
    ? `${model.displayName} (${model.slug})`
    : model.slug;
  return {
    label,
    slug: model.slug,
    displayName: model.displayName,
    contextTokens: model.contextTokens,
    maxOutputTokens: model.maxOutputTokens,
  };
}

export async function loadOfficialModelOptions(): Promise<OfficialModelOption[]> {
  const models = await apiClient.listModels();
  return models.filter((model) => model.isPublished && !isOfficialImageModel(model)).map(officialModelOption);
}

export async function loadOfficialImageOptions(): Promise<OfficialImageOption[]> {
  const models = await apiClient.listModels();
  return models
    .filter((model) => model.isPublished && isOfficialImageModel(model))
    .map((model) => ({
      label: model.displayName && model.displayName !== model.slug
        ? `${model.displayName} (${model.slug})`
        : model.slug,
      slug: model.slug,
      displayName: model.displayName || model.slug,
    }));
}

/** One catalog read for both chat and image selectors. */
export async function loadOfficialCatalog(): Promise<{
  chat: OfficialModelOption[];
  image: OfficialImageOption[];
}> {
  const models = await apiClient.listModels();
  const published = models.filter((model) => model.isPublished);
  return {
    chat: published.filter((model) => !isOfficialImageModel(model)).map(officialModelOption),
    image: published.filter(isOfficialImageModel).map((model) => ({
      label: model.displayName && model.displayName !== model.slug
        ? `${model.displayName} (${model.slug})`
        : model.slug,
      slug: model.slug,
      displayName: model.displayName || model.slug,
    })),
  };
}
